import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Settings,
  User,
  CreditCard,
  Shield,
  Bell,
  ChevronRight,
  Check,
  Crown,
  Zap,
  Star,
  Award,
  Loader2,
  ExternalLink,
  Download,
  FileText,
  LogOut,
  Target,
  Sparkles,
  TrendingUp,
  AlertCircle,
  Eye,
  EyeOff,
  Lock,
  MessageSquare,
  Search,
  ShoppingBag,
  X,
  Clock
} from "lucide-react";

const RECRUITER_TIERS = [
  {
    id: "recruiter_starter",
    name: "Starter",
    price: 29,
    features: ["3 active job postings", "AI job descriptions (10/mo)", "Basic candidate ranking", "Compare 2 candidates", "5 AI interviews/month", "CRM pipeline access"]
  },
  {
    id: "recruiter_pro",
    name: "Pro",
    price: 79,
    features: ["10 active job postings", "Unlimited AI descriptions", "20 AI interviews/month", "Compare 5 candidates", "Save job templates", "Bulk messaging"]
  },
  {
    id: "recruiter_elite",
    name: "Elite",
    price: 149,
    features: ["Unlimited job postings", "Unlimited AI interviews", "Unlimited comparisons", "Priority matching", "Export reports (PDF)", "Advanced analytics"]
  }
];

const JOBSEEKER_TIERS = [
  {
    id: "jobseeker_free",
    name: "Free",
    price: 0,
    features: ["Profile creation & resume upload", "Apply to jobs", "Basic role-fit score", "2 AI practice interviews/mo"]
  },
  {
    id: "jobseeker_plus",
    name: "Plus",
    price: 9,
    features: ["Enhanced visibility", "AI career insights", "10 AI practice interviews/mo", "Job recommendations", "Email digests & reminders"]
  },
  {
    id: "jobseeker_pro",
    name: "Pro",
    price: 19,
    features: ["Priority ranking in searches", "Unlimited AI interviews", "AI feedback & scoring", "Resume optimization", "Skill gap analysis", "Early job access"]
  }
];

const RECRUITER_PAYG = [
  { name: "Promote a Job", desc: "Boost job visibility with priority placement", options: "$29/7d · $49/14d · $79/30d", buyCategory: "promote" },
  { name: "Unlock Candidate Contacts", desc: "Reveal email and phone for candidates", options: "5 for $9 · 15 for $19", buyCategory: "unlocks" },
  { name: "Outreach Credits", desc: "Directly message candidates", options: "10 for $19 · 25 for $39", buyCategory: "outreach" },
  { name: "AI Credits", desc: "Power AI descriptions, interviews, analysis", options: "10 for $5 · 25 for $10 · 50 for $15", buyCategory: "ai_credits" },
];

const JOBSEEKER_PAYG = [
  { name: "AI CV Generation", desc: "Generate a tailored professional CV", options: "Via AI credits", buyCategory: "ai_credits" },
  { name: "AI Interview Practice", desc: "Simulate real interviews with AI feedback", options: "Via AI credits", buyCategory: "ai_credits" },
  { name: "AI Cover Letter", desc: "Personalized cover letter for any job", options: "Via AI credits", buyCategory: "ai_credits" },
  { name: "AI Credits", desc: "Use across all AI-powered tools", options: "10 for $5 · 25 for $10 · 50 for $15", buyCategory: "ai_credits" },
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [changingTier, setChangingTier] = useState(null);
  const [quickActions, setQuickActions] = useState([]);
  const [privacySettings, setPrivacySettings] = useState({
    profile_visibility: "public",
    message_approval: "open",
    show_email: false,
    show_activity: true
  });
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [currencyPreference, setCurrencyPreference] = useState("auto");
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [buyModal, setBuyModal] = useState(null); // null or { category: "ai_credits" | "outreach" | "unlocks" | "promote" }
  const [purchasing, setPurchasing] = useState(null);
  const [creditHistory, setCreditHistory] = useState([]);
  const [showCreditHistory, setShowCreditHistory] = useState(false);

  const CREDIT_PACKS = {
    ai_credits: [
      { id: "ai_credits_10", name: "10 AI Credits", price: 5, credits: 10 },
      { id: "ai_credits_25", name: "25 AI Credits", price: 10, credits: 25, best: true },
      { id: "ai_credits_50", name: "50 AI Credits", price: 15, credits: 50 },
    ],
    outreach: [
      { id: "candidate_outreach_10", name: "10 Outreach Credits", price: 19, credits: 10 },
      { id: "candidate_outreach_25", name: "25 Outreach Credits", price: 39, credits: 25, best: true },
    ],
    unlocks: [
      { id: "contact_unlock_5", name: "5 Contact Unlocks", price: 9, credits: 5 },
      { id: "contact_unlock_15", name: "15 Contact Unlocks", price: 19, credits: 15, best: true },
    ],
    promote: [
      { id: "promoted_job_7", name: "7 Days", price: 29 },
      { id: "promoted_job_14", name: "14 Days", price: 49, best: true },
      { id: "promoted_job_30", name: "30 Days", price: 79 },
    ],
  };

  const handleQuickBuy = async (addonId) => {
    setPurchasing(addonId);
    try {
      const res = await fetch(`${API}/addons/purchase`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ addon_id: addonId, origin_url: window.location.origin }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
        }
      } else {
        toast.error("Failed to start checkout. Please try again.");
      }
    } catch {
      toast.error("Checkout error. Please try again.");
    } finally {
      setPurchasing(null);
    }
  };

  const fetchCreditHistory = async () => {
    try {
      const res = await fetch(`${API}/credits/ai/history`, { credentials: "include" });
      if (res.ok) setCreditHistory(await res.json());
    } catch {}
  };

  const isRecruiter = user?.role === "recruiter";
  const tiers = isRecruiter ? RECRUITER_TIERS : JOBSEEKER_TIERS;
  const currentTier = user?.subscription_tier || (isRecruiter ? "recruiter_starter" : "jobseeker_free");

  useEffect(() => {
    fetchTransactions();
    fetchQuickActions();
    fetchPrivacySettings();
    fetchCurrencyPreference();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API}/payments/transactions`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrivacySettings = async () => {
    try {
      const response = await fetch(`${API}/users/privacy`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setPrivacySettings(data.privacy_settings || {
          profile_visibility: "public",
          message_approval: "open",
          show_email: false,
          show_activity: true
        });
      }
    } catch (error) {
      console.error("Failed to fetch privacy settings:", error);
    }
  };

  const updatePrivacySetting = async (key, value) => {
    setSavingPrivacy(true);
    try {
      const response = await fetch(`${API}/users/privacy`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ [key]: value }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrivacySettings(data.privacy_settings);
        toast.success("Privacy settings updated");
      } else {
        toast.error("Failed to update privacy settings");
      }
    } catch (error) {
      toast.error("Failed to update privacy settings");
    } finally {
      setSavingPrivacy(false);
    }
  };

  const fetchCurrencyPreference = async () => {
    try {
      const response = await fetch(`${API}/users/currency-preference`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCurrencyPreference(data.currency_preference || "auto");
      }
    } catch (error) {
      console.error("Failed to fetch currency preference:", error);
    }
  };

  const updateCurrencyPreference = async (value) => {
    setSavingCurrency(true);
    try {
      const response = await fetch(`${API}/users/currency-preference`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ currency_preference: value }),
      });
      
      if (response.ok) {
        setCurrencyPreference(value);
        toast.success("Payment region updated");
      } else {
        toast.error("Failed to update payment region");
      }
    } catch (error) {
      toast.error("Failed to update payment region");
    } finally {
      setSavingCurrency(false);
    }
  };

  const fetchQuickActions = async () => {
    const actions = [];
    
    try {
      // Check profile completion
      const profileEndpoint = isRecruiter ? `${API}/profile/recruiter` : `${API}/profile/jobseeker`;
      const profileRes = await fetch(profileEndpoint, { credentials: "include" });
      
      if (profileRes.ok) {
        const profile = await profileRes.json();
        
        if (isRecruiter) {
          // Recruiter profile checks
          const fields = ["company_name", "industry", "company_size", "location", "company_description"];
          const completed = fields.filter(f => profile[f]).length;
          const percentage = Math.round((completed / fields.length) * 100);
          
          if (percentage < 100) {
            actions.push({
              id: "complete_profile",
              title: "Complete Your Company Profile",
              description: `${percentage}% complete - Add missing details to attract better candidates`,
              icon: User,
              color: "text-yellow-400",
              link: "/profile/recruiter",
              priority: 1
            });
          }
          
          // Check if they have posted jobs
          const jobsRes = await fetch(`${API}/jobs/my-jobs`, { credentials: "include" });
          if (jobsRes.ok) {
            const jobs = await jobsRes.json();
            if (jobs.length === 0) {
              actions.push({
                id: "post_job",
                title: "Post Your First Job",
                description: "Start attracting candidates by posting a job listing",
                icon: Target,
                color: "text-blue-400",
                link: "/jobs/create",
                priority: 2
              });
            }
          }
        } else {
          // Job seeker profile checks
          const fields = ["headline", "summary", "skills", "experience", "education"];
          const completed = fields.filter(f => profile[f] && (Array.isArray(profile[f]) ? profile[f].length > 0 : true)).length;
          const percentage = Math.round((completed / fields.length) * 100);
          
          if (percentage < 100) {
            actions.push({
              id: "complete_profile",
              title: "Complete Your Profile",
              description: `${percentage}% complete - A complete profile gets 3x more views`,
              icon: User,
              color: "text-yellow-400",
              link: "/profile/build",
              priority: 1
            });
          }
          
          // Check skills
          if (!profile.skills || profile.skills.length < 3) {
            actions.push({
              id: "add_skills",
              title: "Add More Skills",
              description: "Profiles with 5+ skills get better job matches",
              icon: Sparkles,
              color: "text-purple-400",
              link: "/profile/build",
              priority: 2
            });
          }
        }
      }
      
      // Check subscription upgrade opportunity
      const tierInfo = getCurrentTierInfo();
      if (tierInfo.price === 0 || (isRecruiter && currentTier === "recruiter_starter")) {
        actions.push({
          id: "upgrade",
          title: isRecruiter ? "Upgrade to Pro" : "Try Plus for Better Visibility",
          description: isRecruiter 
            ? "Get 10 job postings and unlimited AI features" 
            : "Stand out with enhanced visibility and AI interview practice",
          icon: Crown,
          color: "text-crimson",
          link: null, // Handled inline
          priority: 3
        });
      }
      
    } catch (error) {
      console.error("Failed to fetch quick actions:", error);
    }
    
    // Sort by priority
    actions.sort((a, b) => a.priority - b.priority);
    setQuickActions(actions);
  };

  const changeTier = async (tierId) => {
    setChangingTier(tierId);
    try {
      const response = await fetch(`${API}/payments/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ tier_id: tierId }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          // Redirect to Stripe checkout
          window.location.href = data.checkout_url;
        } else {
          // Free tier - instant update
          setUser(prev => ({ ...prev, subscription_tier: tierId }));
          toast.success("Subscription updated!");
        }
      } else {
        toast.error("Failed to change subscription");
      }
    } catch (error) {
      toast.error("Failed to change subscription");
    } finally {
      setChangingTier(null);
    }
  };

  const getCurrentTierInfo = () => {
    return tiers.find(t => t.id === currentTier) || tiers[0];
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      toast.error("Failed to log out");
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex">
      <Sidebar />

      <main className="flex-1 lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">SETTINGS</h1>
            <p className="text-gray-400 text-sm sm:text-base">Manage your account, billing, and preferences</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Settings */}
            <div className="lg:col-span-2 space-y-6">
              {/* Account Info */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-crimson" />
                  Account Information
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-crimson/20 flex items-center justify-center overflow-hidden">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-8 h-8 text-crimson" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{user?.name || "User"}</h3>
                      <p className="text-gray-400">{user?.email}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 bg-crimson/20 text-crimson text-xs uppercase">
                        {user?.role}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-[#333]">
                    <Link to={isRecruiter ? "/profile/recruiter" : "/profile"}>
                      <Button variant="outline" className="border-[#333] text-white">
                        Edit Profile <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>

              {/* Subscription Plans */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-crimson" />
                  Subscription Plan
                </h2>
                
                <div className="mb-6 p-4 bg-obsidian border border-crimson/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-400">Current Plan</p>
                      <p className="text-xl font-bold text-white">{getCurrentTierInfo().name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-crimson">
                        ${getCurrentTierInfo().price}<span className="text-sm text-gray-400">/mo</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  {tiers.map((tier) => {
                    const isCurrent = tier.id === currentTier;
                    const isUpgrade = tier.price > getCurrentTierInfo().price;
                    
                    return (
                      <div
                        key={tier.id}
                        className={`p-3 sm:p-4 border transition-colors ${
                          isCurrent ? "border-crimson bg-crimson/5" : "border-[#333] hover:border-[#444]"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-bold text-white text-sm sm:text-base">{tier.name}</h3>
                          {isCurrent && (
                            <span className="px-1.5 sm:px-2 py-0.5 bg-crimson text-white text-[10px] sm:text-xs">CURRENT</span>
                          )}
                        </div>
                        <p className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3">
                          ${tier.price}<span className="text-xs sm:text-sm text-gray-400">/mo</span>
                        </p>
                        <ul className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                          {tier.features.map((feature, idx) => (
                            <li key={idx} className="text-gray-400 text-xs sm:text-sm flex items-start gap-1.5 sm:gap-2">
                              <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 flex-shrink-0 mt-0.5" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        {!isCurrent && (
                          <Button
                            onClick={() => changeTier(tier.id)}
                            disabled={changingTier === tier.id}
                            className={`w-full ${isUpgrade ? "bg-crimson hover:bg-crimson-hover" : "bg-gray-700 hover:bg-gray-600"}`}
                            data-testid={`select-${tier.id}-btn`}
                          >
                            {changingTier === tier.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : isUpgrade ? (
                              "Upgrade"
                            ) : (
                              "Downgrade"
                            )}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>

              {/* Pay-as-you-go */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                data-testid="payg-settings-section"
              >
                <h2 className="font-heading text-lg font-bold mb-1 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-crimson" />
                  Pay As You Go
                </h2>
                <p className="text-xs text-gray-500 mb-4">No subscription needed. Buy only what you use.</p>

                {/* Credit Balances */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                  <button onClick={() => setBuyModal({ category: "ai_credits" })} className="p-3 bg-obsidian border border-[#222] text-center hover:border-crimson/40 transition-colors cursor-pointer" data-testid="balance-ai-credits">
                    <p className="text-2xl font-bold text-crimson">{user?.ai_credits || 0}</p>
                    <p className="text-gray-500 text-xs mt-0.5">AI Credits</p>
                    <p className="text-[10px] text-crimson/60 mt-1">Click to buy</p>
                  </button>
                  {isRecruiter && (
                    <>
                      <button onClick={() => setBuyModal({ category: "outreach" })} className="p-3 bg-obsidian border border-[#222] text-center hover:border-crimson/40 transition-colors cursor-pointer" data-testid="balance-outreach">
                        <p className="text-2xl font-bold text-crimson">{user?.outreach_credits || 0}</p>
                        <p className="text-gray-500 text-xs mt-0.5">Outreach Credits</p>
                        <p className="text-[10px] text-crimson/60 mt-1">Click to buy</p>
                      </button>
                      <button onClick={() => setBuyModal({ category: "unlocks" })} className="p-3 bg-obsidian border border-[#222] text-center hover:border-crimson/40 transition-colors cursor-pointer" data-testid="balance-unlocks">
                        <p className="text-2xl font-bold text-crimson">{user?.contact_unlocks || 0}</p>
                        <p className="text-gray-500 text-xs mt-0.5">Contact Unlocks</p>
                        <p className="text-[10px] text-crimson/60 mt-1">Click to buy</p>
                      </button>
                    </>
                  )}
                </div>

                {/* PAYG Items */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(isRecruiter ? RECRUITER_PAYG : JOBSEEKER_PAYG).map((item, i) => (
                    <div key={i} className="p-4 bg-obsidian border border-[#1a1a1a] hover:border-crimson/30 transition-colors flex items-start justify-between" data-testid={`payg-settings-item-${i}`}>
                      <div className="flex-1">
                        <h4 className="text-sm font-bold text-white">{item.name}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                        <p className="text-xs text-crimson font-semibold mt-1.5">{item.options}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setBuyModal({ category: item.buyCategory || "ai_credits" })}
                        className="bg-crimson/20 border border-crimson/30 text-crimson hover:bg-crimson hover:text-white text-xs ml-3 flex-shrink-0"
                        data-testid={`payg-buy-btn-${i}`}
                      >
                        Buy
                      </Button>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* AI Credit Usage History */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18 }}
                data-testid="credit-history-section"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-bold flex items-center gap-2">
                    <Clock className="w-5 h-5 text-crimson" />
                    AI Credit Usage
                  </h2>
                  <Button
                    size="sm"
                    onClick={() => { setShowCreditHistory(!showCreditHistory); if (!showCreditHistory) fetchCreditHistory(); }}
                    variant="outline"
                    className="border-[#333] text-gray-400 text-xs"
                    data-testid="toggle-credit-history"
                  >
                    {showCreditHistory ? "Hide" : "View History"}
                  </Button>
                </div>
                {showCreditHistory && (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {creditHistory.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-4">No AI credit usage yet</p>
                    ) : creditHistory.map((h, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-[#1a1a1a] last:border-0" data-testid={`credit-usage-${i}`}>
                        <div>
                          <p className="text-sm text-white">{h.action || h.feature || "AI Feature"}</p>
                          <p className="text-[10px] text-gray-500">{new Date(h.created_at).toLocaleString()}</p>
                        </div>
                        <span className="text-xs text-crimson font-bold">-1 credit</span>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>

              {/* Billing History */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-crimson" />
                  Billing History
                </h2>
                
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 text-crimson animate-spin mx-auto" />
                  </div>
                ) : transactions.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No billing history yet</p>
                ) : (
                  <div className="space-y-3">
                    {transactions.slice(0, 5).map((tx) => (
                      <div key={tx.transaction_id} className="flex items-center justify-between p-3 bg-obsidian border border-[#333]">
                        <div>
                          <p className="text-white font-medium">{tx.description || tx.tier_id}</p>
                          <p className="text-gray-500 text-sm">
                            {new Date(tx.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-bold">${tx.amount?.toFixed(2) || "0.00"}</p>
                          <span className={`text-xs ${tx.status === "completed" ? "text-green-400" : "text-yellow-400"}`}>
                            {tx.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>

              {/* Privacy Settings */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-crimson" />
                  Privacy Settings
                </h2>
                
                <div className="space-y-4">
                  {/* Profile Visibility */}
                  <div className="p-4 bg-obsidian border border-[#333]">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {privacySettings.profile_visibility === "private" ? (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-green-400" />
                        )}
                        <span className="font-medium text-white">Profile Visibility</span>
                      </div>
                    </div>
                    <p className="text-gray-500 text-sm mb-3">Control who can see your profile</p>
                    <select
                      value={privacySettings.profile_visibility || "public"}
                      onChange={(e) => updatePrivacySetting("profile_visibility", e.target.value)}
                      disabled={savingPrivacy}
                      className="w-full px-3 py-2 bg-obsidian-light border border-[#333] text-white text-sm"
                    >
                      <option value="public">Public - Anyone can view my profile</option>
                      <option value="connections_only">Connections Only</option>
                      <option value="private">Private - Hide my profile</option>
                    </select>
                  </div>

                  {/* Message Approval */}
                  <div className="p-4 bg-obsidian border border-[#333]">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {privacySettings.message_approval === "approval_required" ? (
                          <Lock className="w-4 h-4 text-yellow-400" />
                        ) : (
                          <MessageSquare className="w-4 h-4 text-green-400" />
                        )}
                        <span className="font-medium text-white">Message Requests</span>
                      </div>
                    </div>
                    <p className="text-gray-500 text-sm mb-3">Control who can message you</p>
                    <select
                      value={privacySettings.message_approval || "open"}
                      onChange={(e) => updatePrivacySetting("message_approval", e.target.value)}
                      disabled={savingPrivacy}
                      className="w-full px-3 py-2 bg-obsidian-light border border-[#333] text-white text-sm"
                    >
                      <option value="open">Open - Anyone can message me</option>
                      <option value="approval_required">Approval Required - I must approve before receiving messages</option>
                    </select>
                  </div>

                  {/* Show Email */}
                  <div className="p-4 bg-obsidian border border-[#333] flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-white">Show Email on Profile</span>
                      </div>
                      <p className="text-gray-500 text-sm">Allow others to see your email address</p>
                    </div>
                    <button
                      onClick={() => updatePrivacySetting("show_email", !privacySettings.show_email)}
                      disabled={savingPrivacy}
                      className={`w-12 h-6 rounded-full transition-colors relative ${
                        privacySettings.show_email ? "bg-crimson" : "bg-gray-600"
                      }`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                        privacySettings.show_email ? "right-1" : "left-1"
                      }`} />
                    </button>
                  </div>

                  {/* Show Activity */}
                  <div className="p-4 bg-obsidian border border-[#333] flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-white">Show Activity Status</span>
                      </div>
                      <p className="text-gray-500 text-sm">Show when you were last active</p>
                    </div>
                    <button
                      onClick={() => updatePrivacySetting("show_activity", !privacySettings.show_activity)}
                      disabled={savingPrivacy}
                      className={`w-12 h-6 rounded-full transition-colors relative ${
                        privacySettings.show_activity ? "bg-crimson" : "bg-gray-600"
                      }`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                        privacySettings.show_activity ? "right-1" : "left-1"
                      }`} />
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Payment Region Settings */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-crimson" />
                  Payment Region
                </h2>
                
                <div className="p-4 bg-obsidian border border-[#333]">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-white">Preferred Payment Gateway</span>
                  </div>
                  <p className="text-gray-500 text-sm mb-3">
                    Choose which payment gateway to use for subscriptions. This affects which payment options you see at checkout.
                  </p>
                  <select
                    value={currencyPreference}
                    onChange={(e) => updateCurrencyPreference(e.target.value)}
                    disabled={savingCurrency}
                    className="w-full px-3 py-2 bg-obsidian-light border border-[#333] text-white text-sm"
                    data-testid="currency-preference-select"
                  >
                    <option value="auto">Auto-detect (based on your location)</option>
                    <option value="USD">International (USD - Stripe)</option>
                    <option value="NGN">Nigeria (NGN - Paystack/Flutterwave)</option>
                  </select>
                  <p className="text-gray-600 text-xs mt-2">
                    {currencyPreference === "auto" && "We'll detect your location to show the best payment options"}
                    {currencyPreference === "USD" && "You'll see Stripe checkout with USD pricing"}
                    {currencyPreference === "NGN" && "You'll see Paystack & Flutterwave options with NGN pricing"}
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions - Profile Completion & Upgrade */}
              {quickActions.length > 0 && (
                <motion.div
                  className="bg-obsidian-light border border-crimson/30 p-6"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-crimson" />
                    Quick Actions
                  </h3>
                  <div className="space-y-3">
                    {quickActions.map((action) => (
                      action.link ? (
                        <Link
                          key={action.id}
                          to={action.link}
                          className="block p-4 bg-obsidian border border-[#333] hover:border-crimson/50 transition-colors group"
                          data-testid={`quick-action-${action.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 bg-obsidian-light flex items-center justify-center flex-shrink-0 ${action.color}`}>
                              <action.icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                              <p className="text-white font-medium group-hover:text-crimson transition-colors">{action.title}</p>
                              <p className="text-gray-500 text-sm mt-1">{action.description}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-crimson transition-colors mt-1" />
                          </div>
                        </Link>
                      ) : (
                        <div
                          key={action.id}
                          className="p-4 bg-gradient-to-r from-crimson/10 to-crimson/5 border border-crimson/30"
                          data-testid={`quick-action-${action.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                              <action.icon className="w-4 h-4 text-crimson" />
                            </div>
                            <div className="flex-1">
                              <p className="text-white font-medium">{action.title}</p>
                              <p className="text-gray-400 text-sm mt-1">{action.description}</p>
                              <Button
                                onClick={() => {
                                  const upgradeTier = isRecruiter ? "recruiter_pro" : "jobseeker_plus";
                                  changeTier(upgradeTier);
                                }}
                                size="sm"
                                className="mt-3 bg-crimson hover:bg-crimson-hover text-white"
                              >
                                <TrendingUp className="w-3 h-3 mr-1" />
                                Upgrade Now
                              </Button>
                            </div>
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Quick Links */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <h3 className="font-heading text-lg font-bold mb-4">Quick Links</h3>
                <div className="space-y-2">
                  <Link to="/search/users" className="flex items-center justify-between p-3 bg-obsidian border border-[#333] hover:border-[#444] transition-colors">
                    <span className="flex items-center gap-2 text-gray-300">
                      <Search className="w-4 h-4" /> Find People
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </Link>
                  <Link to="/notifications/settings" className="flex items-center justify-between p-3 bg-obsidian border border-[#333] hover:border-[#444] transition-colors">
                    <span className="flex items-center gap-2 text-gray-300">
                      <Bell className="w-4 h-4" /> Notifications
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </Link>
                  {isRecruiter && (
                    <Link to="/profile/recruiter" className="flex items-center justify-between p-3 bg-obsidian border border-[#333] hover:border-[#444] transition-colors">
                      <span className="flex items-center gap-2 text-gray-300">
                        <Shield className="w-4 h-4" /> Company Profile
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </Link>
                  )}
                  <Link to="/calendar" className="flex items-center justify-between p-3 bg-obsidian border border-[#333] hover:border-[#444] transition-colors">
                    <span className="flex items-center gap-2 text-gray-300">
                      <FileText className="w-4 h-4" /> Calendar
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </Link>
                </div>
              </motion.div>

              {/* Logout */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log Out
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </main>

      {/* Quick Buy Modal */}
      <AnimatePresence>
        {buyModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setBuyModal(null)} />
            <motion.div
              className="relative bg-obsidian-light border border-[#333] w-full max-w-md p-6"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              data-testid="quick-buy-modal"
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                  <Zap className="w-5 h-5 text-crimson" />
                  {buyModal.category === "ai_credits" && "Buy AI Credits"}
                  {buyModal.category === "outreach" && "Buy Outreach Credits"}
                  {buyModal.category === "unlocks" && "Buy Contact Unlocks"}
                  {buyModal.category === "promote" && "Promote a Job"}
                </h3>
                <button onClick={() => setBuyModal(null)} className="text-gray-500 hover:text-white transition-colors" data-testid="close-buy-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3">
                {(CREDIT_PACKS[buyModal.category] || []).map((pack) => (
                  <div
                    key={pack.id}
                    className={`p-4 border transition-colors ${pack.best ? "border-crimson bg-crimson/5" : "border-[#222] hover:border-[#444]"}`}
                    data-testid={`pack-${pack.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-bold text-white">{pack.name}</h4>
                          {pack.best && <span className="text-[9px] px-1.5 py-0.5 bg-crimson text-white font-bold">BEST VALUE</span>}
                        </div>
                        {pack.credits && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            ${(pack.price / pack.credits).toFixed(2)} per credit
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleQuickBuy(pack.id)}
                        disabled={purchasing === pack.id}
                        className="bg-crimson hover:bg-crimson/80 text-white text-sm px-4"
                        data-testid={`buy-${pack.id}`}
                      >
                        {purchasing === pack.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          `$${pack.price}`
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-[10px] text-gray-600 mt-4 text-center">
                Secure checkout powered by Stripe. Credits are added instantly after payment.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SettingsPage;
